import {
  LitElement,
  html,
  css,
  styleMap,
  classMap,
  styles,
  t,
} from '@hh/global.js'

class HHPagination extends LitElement {
  static properties = {
    loading: {
      reflect: true,
      type: Boolean,
    },
    totalItems: {
      attribute: 'total-items',
      type: Number,
    },
    totalPages: {
      attribute: 'total-pages',
      type: Number,
    },
    currentParamsPage: {
      attribute: 'current-params-page',
      type: Number,
    },
    perPage: {
      attribute: 'per-page',
      type: Number,
    },
    listSelector: {
      attribute: 'list-selector',
      type: String,
    },
    cardSelector: {
      attribute: 'card-selector',
      type: String,
    },
    hiddenCardSelector: {
      attribute: 'hidden-card-selector',
      type: String,
    },
    hiddenItemSelector: {
      attribute: 'hidden-item-selector',
      type: String,
    },
    searchType: {
      attribute: 'data-search-type',
      reflect: true,
      type: String,
    },
    tVerb: {
      attribute: 't-verb',
      type: String,
    },
    tSeparator: {
      attribute: 't-separator',
      type: String,
    },
    tAria: {
      attribute: 't-aria',
      type: String,
    },
    _currentPage: {
      state: true,
      type: Number,
    },
    _isLastPage: {
      state: true,
    },
    _loadedItems: {
      state: true,
      type: Number,
    },
    sectionID: {
      attribute: 'section-id',
      type: String,
    },
  }

  constructor() {
    super()
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    this.url = new URL(window.location.href);
    this.params = new URLSearchParams(this.url.search)
    this.currentQueryType = this.params.get("type");
  }

  connectedCallback() {
    super.connectedCallback()
    this._currentPage = this.currentParamsPage
    this.loading = false
    this._calculateItemsSize()
    this.updateComplete.then(() => {
      this._checkLastPage()
    })
    setTimeout(() => {
      this._updateNostoAriaLabels()
    }, 1000)

    this.searchTypeString = `${t('sections.collection_template.product_count.products')}`
    if (this.searchType === 'pages') {
      this.searchTypeString = `${t('sections.collection_template.search_type.pages')}`
    } else if (this.searchType === 'articles') {
      this.searchTypeString = `${t('sections.collection_template.search_type.articles')}`
    }

  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  updated(changedProperties) {
    if (changedProperties.has('_currentPage')) {
      this._checkLastPage()
      if (this._isLastPage && this.hiddenCardSelector) {
        const hiddenProductsSelector = this.hiddenCardSelector + '.hidden'
        this._showHiddenProductsPage()
        this._isLastPage =
          document.querySelectorAll(hiddenProductsSelector).length < 1
      }
      this._calculateItemsSize()
    }
    if (changedProperties.has('loading')) {
      this.toggleButtonLoading()
    }
  }

  _calculateItemsSize() {
    const productGrid = document.querySelector(this.listSelector)
    this._loadedItems = productGrid.querySelectorAll(
      `${this.cardSelector}:not(.nosto_element):not(.hidden)${
        this.hiddenItemSelector ? ':not(' + this.hiddenItemSelector + ')' : ''
      }`
    ).length
  }

  async onLoadMore(e) {
    e.preventDefault()
    this.loading = true
    this.dispatchEvent(new CustomEvent('load-more'))
    try {
      const nextPage = await this._fetchPage(this._currentPage + 1)
      this._appendProducts(nextPage)
      this._currentPage++
      yotpoWidgetsContainer.initWidgets(false)
    } catch (e) {
      console.error({ error: e })
    } finally {
      setTimeout(() => {
        if (this.isSafari) {
          document.querySelectorAll('.blur-image--loading:not(.loaded)').forEach((image) => {
            // get Image ID attribute
            const parentWrapperDataId = image
              ?.closest('.js-product-card')
              ?.getAttribute('data-id')


            const imageID = image?.getAttribute('id')
            const blurImage = document.querySelector(
              `#${imageID}.blur-image--loading`
            )

            const loadedImage = document.querySelector(
              `.js-product-card[data-id="${parentWrapperDataId}"] #${imageID}:not(.blur-image--loading)`
            )

            if (loadedImage?.complete) {
              this.loaded(blurImage, loadedImage, imageID)
            } else {
              loadedImage?.addEventListener(
                'load', (e) =>
                this.loaded(blurImage, loadedImage, imageID)
              )
            }
          })
        }
      }, 500);
      this.loading = false
    }
  }

  _appendProducts(doc) {
    const productGrid = document.querySelector(this.listSelector)
    const products = doc.querySelectorAll(this.cardSelector)
    const outOfStockProduct = document.querySelector(this.hiddenCardSelector)

    products.forEach((product) => {
      const alreadyExists = productGrid.querySelector(
        `${this.cardSelector}[data-id="${product.dataset?.id || ''}"]${
          this.hiddenItemSelector ? ':not(' + this.hiddenItemSelector + ')' : ''
        }`
      )
      if (!alreadyExists) {
        if (outOfStockProduct) {
          outOfStockProduct.insertAdjacentElement('beforebegin', product)
          return
        }

        productGrid.appendChild(product)
      }
    })

    const firstNewProductCard = products[0]
    setTimeout(() => {
      firstNewProductCard?.querySelector('a').focus({ preventScroll: true })
    }, 0)

    if (!firstNewProductCard && outOfStockProduct) {
      const hiddenProductsSelector = this.hiddenCardSelector + '.hidden'
      this._showHiddenProductsPage()
      this._isLastPage =
        document.querySelectorAll(hiddenProductsSelector).length < 1
      this._calculateItemsSize()
    }
    // Fix for Safari since it has a bug with srcset on dynamically added images
    if (this.isSafari) {
      this._fixSafariSrcsetBug(productGrid)
    }
  }

  loaded(blurImage, loadedImage, imageID) {
    loadedImage.style.display = 'block'
    setTimeout(() => {
      document
        .querySelector(`#${imageID}.blur-image--loading`)
        .classList.add('loaded')
      // blurImage.remove()
    }, 1000)

    return
  }

  _showHiddenProductsPage() {
    const hiddenProductsSelector = this.hiddenCardSelector + '.hidden'
    const hiddenProducts = document.querySelectorAll(hiddenProductsSelector)
    for (let i = 0; i < this.perPage; i++) {
      const hiddenProduct = hiddenProducts[i]
      if (!hiddenProduct) {
        continue
      }
      hiddenProduct.removeAttribute('aria-hidden')
      hiddenProduct.classList.remove('hidden')
    }
  }

  _updateNostoAriaLabels() {
    const productGrid = document.querySelector(this.listSelector)
    const nostoItems = productGrid.querySelectorAll(
      this.cardSelector + '.nosto_element'
    )
    const nostoAriaLabel = t('accessibility.information')
    nostoItems.forEach((nostoItem) => {
      nostoItem.setAttribute('aria-label', nostoAriaLabel)
    })
  }

  _checkLastPage() {
    if (this._currentPage !== this.totalPages) return
    this._isLastPage = true
  }

  async _fetchPage(page) {
    try {
      const url = new URL(window.location.href)
      if (this.sectionID) {
        url.searchParams.set('section_id', this.sectionID)
      }
      url.searchParams.set('page', page)
      const response = await fetch(url)
      if (!response.ok) throw new Error(response.statusText)

      const data = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(data, 'text/html')
      return doc
    } catch (error) {
      console.error(error)
    }
  }

  _fixSafariSrcsetBug(grid) {
    const products = [...grid.querySelectorAll(this.cardSelector)].slice(
      this._currentPage * this.perPage,
      this._currentPage * (this.perPage * 2)
    )
    if (products.length === 0) return

    products.forEach((product) => {
      const productImages = product.querySelectorAll('img')
      productImages.forEach((image) => (image.outerHTML = image.outerHTML))
    })
  }

  toggleButtonLoading() {
    if (!this.button) return
    if (this.loading) {
      this.button.setAttribute('loading', '')
      this.button.setAttribute('disabled', '')
    } else {
      this.button.removeAttribute('loading')
      this.button.removeAttribute('disabled')
    }
  }

  get button() {
    return this.querySelector('hh-button') ?? null
  }

  render() {
    this.searchTypeString = `${t('sections.collection_template.product_count.products')}`
    if (this.searchType === 'page') {
      this.searchTypeString = `${t('sections.collection_template.search_type.pages')}`
    } else if (this.searchType === 'article') {
      this.searchTypeString = `${t('sections.collection_template.search_type.articles')}`
    }

    const progressString = `${this._loadedItems || 0} ${t(
      'sections.collection_template.product_count.separator'
    )} ${this.totalItems} ${this.searchTypeString}`

    const progressBar =
      Math.round(((this.perPage * this._currentPage) / this.totalItems) * 100) +
      '%'

    return html`
      <div>
        <span
          class="progress-bar"
          role="progressbar"
          aria-valuenow=${parseInt(progressBar) > 100
            ? '100'
            : progressBar.replace('%', '')}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span
            class="progress-bar__inner"
            style=${styleMap({ width: progressBar })}
            role="presentation"
          ></span>
        </span>
        <p class="progress-string" role="status">
          <span>${progressString}</span>
        </p>
        <div
          class="${classMap({
            'trigger-wrapper': true,
            'is-hidden': this._isLastPage,
            'is-loading': this.loading,
          })}"
        >
          <slot name="trigger" @click=${this.onLoadMore}></slot>
        </div>
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin: var(--hh-spacing-4xl) 0 0;
      }

      .trigger-wrapper {
        display: flex;
        justify-content: center;
        margin: var(--hh-spacing-xl) 0 0;
      }

      .trigger-wrapper.is-hidden {
        display: none;
      }

      .trigger-wrapper.is-loading {
        pointer-events: none;
      }

      .progress-string {
        font: var(--hh-body-bold-font);
        letter-spacing: var(--hh-body-bold-letter-spacing);
        text-align: center;
        margin: var(--hh-spacing-2xl) 0 0;
      }

      .progress-bar {
        display: block;
        position: relative;
        width: 22rem;
        height: 0.6rem;
        overflow: hidden;
        background-color: var(--hh-color-day);
        border-radius: 0.3rem;
      }

      .progress-bar__inner {
        display: block;
        position: absolute;
        inset: 0;
        background-color: var(--hh-color-brand-primary);
      }
    `,
  ]
}

if (!customElements.get('hh-pagination')) {
  customElements.define('hh-pagination', HHPagination);
}